import React, { useState } from 'react';
import { useDevelopmentDemo } from '../../contexts/DevelopmentDemoContext';
import type { ReferralDraft } from '../../types/developmentDemo';
import { DemoSectionHeader } from '../demoShell/DemoSectionHeader';
import { errorClass, fieldClass, glassPanel, labelClass, primaryButton } from '../../utils/demoUi';
import { validateReferralDraft } from '../../utils/developmentDemo';
import { ReferralCard } from './ReferralCard';
export const referralLocalNotice = 'This records a fictional referral locally. No message or record is sent to another organization.';
export function PartnerReferrals() {
  const {
    state,
    config,
    dispatch
  } = useDevelopmentDemo();
  const draft = state.ui.referralDraft;
  const [attempted, setAttempted] = useState(false);
  const [message, setMessage] = useState('');
  const errors = attempted ? validateReferralDraft(draft) : {};
  const update = (patch: Partial<ReferralDraft>) => {
    setMessage('');
    dispatch({
      type: 'setReferralDraft',
      draft: patch
    });
  };
  const errorFor = (key: keyof ReferralDraft) => errors[key] ? <p className={errorClass} data-id="element-841" data-mp="140ceac2jfv48">{errors[key]}</p> : null;
  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setAttempted(true);
    if (Object.keys(validateReferralDraft(draft)).length > 0) return;
    dispatch({
      type: 'createReferral'
    });
    setAttempted(false);
    setMessage('Referral recorded as Pending.');
  };
  return <div data-id="element-842" data-mp="1mw2epuutojg0">
      <DemoSectionHeader title="Partner Referrals" description="Share only what a partner needs, for a stated purpose, with the participant’s agreement." data-id="element-843" data-mp="1mvszn2bbfwp3" />
      <p className="mb-6 rounded-lg border-l-4 border-teal bg-teal-pale px-4 py-3 text-sm text-ink-dark" data-id="element-844" data-mp="1wmnn2a0at2c4">
        {referralLocalNotice} A referral never grants access to the participant’s complete record.
      </p>

      <div className="grid grid-cols-1 gap-8 xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]" data-id="element-845" data-mp="25n3oldxhdzec">
        <form onSubmit={submit} noValidate aria-labelledby="new-referral" className={`${glassPanel} h-fit p-6`} data-id="element-846" data-mp="21m9ws9gx40g6">
          <h2 id="new-referral" className="font-heading text-lg font-bold text-ink-dark" data-id="element-847" data-mp="28pzr9yb1qaun">
            New referral
          </h2>
          <label htmlFor="ref-participant" className={`mt-4 ${labelClass}`} data-id="element-848" data-mp="2dot2tl6rvrs6">
            Participant
          </label>
          <select id="ref-participant" value={draft.participantId} onChange={event => update({
          participantId: event.target.value
        })} aria-invalid={Boolean(errors.participantId)} className={fieldClass} data-id="element-849" data-mp="2ilb7ym60ls51">
            <option value="" data-id="element-850" data-mp="2ri2a9gb5u382">Choose…</option>
            {config.participants.map(participant => <option key={participant.id} value={participant.id} data-id="element-851" data-mp="2wgm03rsvjz5p">
                {participant.name} — {participant.description}
              </option>)}
          </select>
          {errorFor('participantId')}

          <label htmlFor="ref-partner" className={`mt-4 ${labelClass}`} data-id="element-852" data-mp="170e53tb5nu2q">
            Receiving organization
          </label>
          <select id="ref-partner" value={draft.partnerId} onChange={event => update({
          partnerId: event.target.value
        })} aria-invalid={Boolean(errors.partnerId)} className={fieldClass} data-id="element-853" data-mp="1bzz3a1ii3yee">
            <option value="" data-id="element-854" data-mp="3ki8q5rd0i6n4">Choose…</option>
            {config.partners?.map(partner => <option key={partner.id} value={partner.id} data-id="element-855" data-mp="3gg435bh2k89g">
                {partner.name} — {partner.offers}
              </option>)}
          </select>
          {errorFor('partnerId')}

          <label htmlFor="ref-purpose" className={`mt-4 ${labelClass}`} data-id="element-856" data-mp="c2ywn7n6u9qy">
            Referral purpose
          </label>
          <input id="ref-purpose" value={draft.purpose} onChange={event => update({
          purpose: event.target.value
        })} aria-invalid={Boolean(errors.purpose)} className={fieldClass} data-id="element-857" data-mp="82ewrwb8cs14" />
          {errorFor('purpose')}

          <label htmlFor="ref-shared" className={`mt-4 ${labelClass}`} data-id="element-858" data-mp="3u823oxwpsvu0">
            Exact information to share
          </label>
          <p className="text-xs text-ink-muted" data-id="element-859" data-mp="2it51embisa4p">Write the summary exactly as the partner will see it.</p>
          <textarea id="ref-shared" rows={4} value={draft.sharedInfo} onChange={event => update({
          sharedInfo: event.target.value
        })} aria-invalid={Boolean(errors.sharedInfo)} className={fieldClass} data-id="element-860" data-mp="akgrtzqdjd47" />
          {errorFor('sharedInfo')}

          <label className="mt-4 flex items-start gap-3 rounded-lg border border-line bg-surface-bg p-3 text-sm text-ink-dark" data-id="element-861" data-mp="1ktmx3yfrp6e">
            <input type="checkbox" checked={draft.consent} onChange={event => update({
            consent: event.target.checked
          })} className="mt-0.5 h-4 w-4 accent-[#6366C9]" data-id="element-862" data-mp="6j3ivgrzitt2" />
            The participant agreed to this purpose and to sharing exactly this information.
          </label>
          {errorFor('consent')}

          <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-863" data-mp="kccsnijuhhc4">
            <button type="submit" className={primaryButton} data-id="element-864" data-mp="p81qnhg7uq6b">
              Record referral
            </button>
            <p role="status" className="text-sm font-medium text-navy" data-id="element-865" data-mp="34z64vtxr9qa1">
              {message}
            </p>
          </div>
        </form>

        <section aria-labelledby="referral-list" data-id="element-866" data-mp="32fszhzxy5j0j">
          <h2 id="referral-list" className="font-heading text-lg font-bold text-ink-dark" data-id="element-867" data-mp="32ehxo5o2alc2">
            Referrals ({state.referrals.length})
          </h2>
          {state.referrals.length === 0 ? <p className="mt-2 text-sm text-ink-muted" data-id="element-868" data-mp="3bg7p3bhy1k9u">No referrals recorded yet.</p> : <ul className="mt-3 space-y-4" data-id="element-869" data-mp="36i68cx7zpx1j">
              {[...state.referrals].reverse().map(referral => <li key={referral.id} data-id="element-870" data-mp="2ipzec4pqm25a">
                  <ReferralCard referral={referral} canUpdate data-id="element-871" data-mp="2er0ayojnmdfy" />
                </li>)}
            </ul>}
        </section>
      </div>
    </div>;
}